// Panel de gestion de la photo de profil dans le Dashboard.
// - Aperçu de la photo actuelle (via <Avatar>)
// - Bouton "Modifier" ouvre le sélecteur de fichier
// - Preview avant confirmation
// - Suppression (retire la photo, revient aux initiales)
//
// Le backend accepte du base64 avec ou sans préfixe data:URI et resize en 512x512.
import React, { useRef, useState } from "react";
import Avatar from "./Avatar";
import { uploadProfilePhoto, deleteProfilePhoto } from "../services/authService";

const MAX_SIZE_MB = 5;

export default function ProfilePhotoPanel({ user, onUpdated }) {
	const inputRef = useRef(null);
	const [preview, setPreview] = useState(null);      // dataURL local avant upload
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState("");

	const currentPhoto = preview || user?.photoProfil || null;

	const openPicker = () => inputRef.current?.click();

	const handleFile = (e) => {
		setError("");
		const file = e.target.files?.[0];
		e.target.value = ""; // reset pour rechoisir le même fichier
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			setError("Le fichier doit être une image.");
			return;
		}
		if (file.size > MAX_SIZE_MB * 1024 * 1024) {
			setError(`L'image dépasse ${MAX_SIZE_MB} Mo.`);
			return;
		}

		const reader = new FileReader();
		reader.onload = () => setPreview(reader.result);
		reader.onerror = () => setError("Impossible de lire l'image.");
		reader.readAsDataURL(file);
	};

	const handleConfirm = async () => {
		if (!preview) return;
		setUploading(true);
		setError("");
		const result = await uploadProfilePhoto(preview);
		setUploading(false);
		if (result.success) {
			setPreview(null);
			onUpdated?.(result.photoUrl);
		} else {
			setError(result.error || "Erreur lors de l'upload.");
		}
	};

	const handleCancelPreview = () => {
		setPreview(null);
		setError("");
	};

	const handleDelete = async () => {
		if (!user?.photoProfil) return;
		if (!window.confirm("Retirer ta photo de profil ?")) return;
		setUploading(true);
		setError("");
		const result = await deleteProfilePhoto();
		setUploading(false);
		if (result.success) {
			onUpdated?.(null);
		} else {
			setError(result.error || "Erreur lors de la suppression.");
		}
	};

	return (
		<div className="dashboard-info profile-photo-panel">
			<h3>Photo de profil</h3>

			<div className="profile-photo-row">
				<div className="profile-photo-avatar">
					{preview ? (
						<img src={preview} alt="Aperçu" className="profile-photo-preview" />
					) : (
						<Avatar
							prenom={user?.prenom}
							nom={user?.nom}
							photo={user?.photoProfil}
							size={92}
						/>
					)}
				</div>

				<div className="profile-photo-actions">
					{!preview ? (
						<>
							<button
								className="dashboard-password-button"
								onClick={openPicker}
								disabled={uploading}
							>
								{user?.photoProfil ? "Changer la photo" : "Ajouter une photo"}
							</button>
							{user?.photoProfil && (
								<button
									className="dashboard-password-cancel"
									onClick={handleDelete}
									disabled={uploading}
								>
									Retirer
								</button>
							)}
						</>
					) : (
						<>
							<button
								className="dashboard-password-button"
								onClick={handleConfirm}
								disabled={uploading}
							>
								{uploading ? "Envoi..." : "Enregistrer cette photo"}
							</button>
							<button
								className="dashboard-password-cancel"
								onClick={handleCancelPreview}
								disabled={uploading}
							>
								Annuler
							</button>
						</>
					)}
					<p className="profile-photo-hint">
						JPG ou PNG, {MAX_SIZE_MB} Mo max. Elle sera redimensionnée à 512×512.
					</p>
				</div>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				style={{ display: "none" }}
				onChange={handleFile}
			/>

			{error && <p className="profile-photo-error">{error}</p>}
		</div>
	);
}
